import { ReactNode, useRef, useState } from 'react';
import { useResizeObserver } from '@ardoq/hooks';
import {
  truncateComponentLabel,
  getFullComponentLabel,
  ComponentLabelParts,
} from '@ardoq/graph';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { BULK_POPOVER_ATTR, TruncationPopoverContent } from '@ardoq/popovers';

type GetFullLabelTextArgs = {
  label: string | null;
  sublabelValue?: string | null;
  sublabelKey?: string | null;
};
const getFullLabelText = ({
  label,
  sublabelValue,
  sublabelKey,
}: GetFullLabelTextArgs) => {
  if (!label) {
    return '';
  }
  if (!sublabelValue) {
    return label;
  }
  return `${label} [${sublabelKey ? `${sublabelKey}: ` : ''}${sublabelValue}]`;
};

export const resolveTruncatedComponentLabelPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null => {
  const componentElement = element.closest(`[${COMPONENT_ID_ATTRIBUTE}]`);
  const componentId = componentElement?.getAttribute(COMPONENT_ID_ATTRIBUTE);
  if (!componentId) {
    return null;
  }
  const fullLabel = getFullComponentLabel(componentId);
  if (!fullLabel) {
    return null;
  }
  return <TruncationPopoverContent>{fullLabel}</TruncationPopoverContent>;
};

type SelfTruncatingComponentLabelProperties = ComponentLabelParts & {
  measure: (label: string) => number;
  bypassRenderLimit?: boolean;
  /**
   * Emit the `data-bulk-popover` trigger for the full-label popover when the
   * label is truncated. Set to false where an ancestor already renders its own
   * hover popover for this label (avoids two popovers on one hover).
   */
  showTruncationPopover: boolean;
};
const SelfTruncatingComponentLabel = ({
  label,
  fieldLabel,
  fieldValue,
  measure,
  bypassRenderLimit,
  showTruncationPopover,
}: SelfTruncatingComponentLabelProperties) => {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useResizeObserver(container, ({ width }) => {
    setWidth(width);
  });

  const fullLabelText = getFullLabelText({
    label,
    sublabelValue: fieldValue,
    sublabelKey: fieldLabel,
  });

  if (bypassRenderLimit) {
    return <div>{fullLabelText}</div>;
  }

  const truncatedLabel = width
    ? truncateComponentLabel({
        fieldValue,
        fieldLabel,
        label,
        width,
        measure,
      })
    : fullLabelText;

  const isTruncated = truncatedLabel !== fullLabelText;

  return (
    <div
      ref={container}
      style={{ width: '100%' }}
      {...(isTruncated && showTruncationPopover
        ? { [BULK_POPOVER_ATTR]: 'true' }
        : {})}
    >
      {truncatedLabel}
    </div>
  );
};

export { SelfTruncatingComponentLabel };
